import { Portal, Root, Content, Overlay } from '@radix-ui/react-dialog';
import { Children, isValidElement, useCallback, useMemo, useState } from 'react';

import { ModalBack } from './components/modal-back';
import { ModalBody } from './components/modal-body';
import { ModalClose } from './components/modal-close';
import { ModalContent } from './components/modal-content';
import { ModalFooter } from './components/modal-footer';
import { ModalFooterAction } from './components/modal-footer-action';
import { ModalForm } from './components/modal-form';
import { ModalHeader } from './components/modal-header';
import { ModalMedia } from './components/modal-media';
import { ModalSkip } from './components/modal-skip';
import { ModalSubtitle } from './components/modal-subtitle';
import { ModalTitle } from './components/modal-title';
import { ModalTrigger } from './components/modal-trigger';
import { ModalProvider } from './modal.context';
import styles from './modal.module.css';
import { ModalProps } from './types';

const Modal = ({ children, isFullscreenOnMobile = true, size, onInteractOutside, ...rootProps }: ModalProps) => {
  const { trigger, otherChildren } = useMemo(() => {
    const filteredChildren = Children.toArray(children).filter(isValidElement);
    return {
      trigger: filteredChildren.find((child) => child.type === Modal.Trigger),
      otherChildren: filteredChildren.filter((child) => child.type !== Modal.Trigger),
    };
  }, [children]);

  const [hasSkipButton, setHasSkipButton] = useState(false);

  const handleInteractOutside = useCallback<NonNullable<ModalProps['onInteractOutside']>>(
    (e: any) => {
      onInteractOutside?.(e);
      const isDismissible = !hasSkipButton;

      if (!isDismissible) {
        e.preventDefault?.();
      }
    },
    [hasSkipButton, onInteractOutside],
  );

  return (
    <ModalProvider
      value={{
        mountSkipButton: () => setHasSkipButton(true),
      }}
    >
      <Root {...rootProps}>
        {trigger}

        <Portal>
          <Overlay className={styles.overlay} data-isFullscreenOnMobile={isFullscreenOnMobile ? 'true' : 'false'}>
            <Content
              className={styles.content}
              data-isFullscreenOnMobile={isFullscreenOnMobile ? 'true' : 'false'}
              data-size={size}
              onInteractOutside={handleInteractOutside as any}
              onEscapeKeyDown={handleInteractOutside as any}
            >
              {otherChildren}
            </Content>
          </Overlay>
        </Portal>
      </Root>
    </ModalProvider>
  );
};

Modal.Trigger = ModalTrigger;
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Subtitle = ModalSubtitle;
Modal.Skip = ModalSkip;
Modal.Back = ModalBack;
Modal.Close = ModalClose;
Modal.Media = ModalMedia;
Modal.Body = ModalBody;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.FooterAction = ModalFooterAction;
Modal.Form = ModalForm;

export default Modal;
